/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import React from 'react';
import type { FieldArrayWithId } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { ReactSortable } from 'react-sortablejs';
import {
  ReactHookFormHiddenInput,
  ReactHookFormTextField,
} from '@opengeoweb/form-fields';
import { CustomIconButton, ToggleMenu } from '@opengeoweb/shared';
import {
  ChevronDown,
  ChevronUp,
  Delete,
  DragHandle,
  Options,
  Visibility,
  VisibilityOff,
} from '@opengeoweb/theme';
import type { DrawingObject } from './drawingObjectUtils';
import {
  MAX_OBJECT_NAME_LENGTH,
  isDuplicateObjectName,
} from './drawingObjectUtils';
import type {
  DrawingToolFormValues,
  DrawingToolObjectFormValue,
} from './DrawingToolFormContents';
import { useWarningsTranslation } from '../../utils/i18n';
import { getUtcDateLabel } from '../../utils/warningUtils';

export interface DrawingObjectActions {
  onSelect: (featureId: string) => void;
  onDeselect: (featureId: string) => void;
  onHover: (featureId: string | undefined) => void;
  onRename: (featureId: string, objectName: string) => void;
  onDelete: (featureId: string) => void;
  onToggleVisibility: (featureId: string) => void;
  onReorder: (featureIds: string[]) => void;
}

const hoverSx = {
  backgroundColor: 'geowebColors.cards.cardContainerMouseOver',
  '.objectOptions, .objectDragHandle': { opacity: 1 },
};

// :focus-visible so a clicked button doesn't keep the row highlighted
const hoverStates =
  '&:hover, &:has(:focus-visible), &:has([aria-expanded="true"])';

// selected objects are the ones being edited on the map
const selectedSx = {
  backgroundColor: 'geowebColors.buttons.primary.default.fill',
  color: 'geowebColors.buttons.primary.default.color',
  '.MuiInputBase-root, .MuiFormHelperText-root.Mui-error': { color: 'inherit' },
  '.MuiInputBase-input::selection': {
    backgroundColor: 'rgba(255, 255, 255, 0.35)',
  },
  // CustomIconButton has no props for this look, so out-specify its own colours
  '.MuiIconButton-root.MuiIconButton-root': {
    color: 'inherit',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'geowebColors.buttons.primary.mouseOver.fill',
    },
  },
};

const inlineInputSx = { fontSize: 14, padding: '2px 4px', borderRadius: 0.5 };

const objectNameSx = (isHidden?: boolean): object => ({
  '.MuiInputBase-input': {
    ...inlineInputSx,
    color: 'inherit',
    cursor: 'text',
    fontStyle: 'italic',
    opacity: isHidden ? 0.5 : 1,
    '&.Mui-disabled': { WebkitTextFillColor: 'currentcolor' },
  },
  '.MuiFormHelperText-root': { marginX: 0.5, marginTop: 0.25 },
});

interface ObjectRowProps {
  index: number;
  drawingObject: DrawingObject;
  isLoading?: boolean;
  isSelected: boolean;
  objectActions: DrawingObjectActions;
}

const ObjectRow: React.FC<ObjectRowProps> = ({
  index,
  drawingObject: { featureId, featureIndex, objectName, isHidden },
  isLoading,
  isSelected,
  objectActions: {
    onSelect,
    onDeselect,
    onHover,
    onRename,
    onDelete,
    onToggleVisibility,
  },
}) => {
  const { t } = useWarningsTranslation();
  const { trigger } = useFormContext();
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  const onBlurName = (event: React.FocusEvent<HTMLInputElement>): void => {
    void trigger('features');
    if (event.target.value !== objectName) {
      onRename(featureId, event.target.value);
    }
  };

  return (
    <Box
      component="li"
      data-testid={`drawingToolForm-object-${featureIndex}`}
      onMouseEnter={(): void => onHover(featureId)}
      onMouseLeave={(): void => onHover(undefined)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 32,
        paddingX: 0.5,
        color: 'geowebColors.typographyAndIcons.text',
        '.objectOptions, .objectDragHandle': { opacity: 0 },
        [hoverStates]: hoverSx,
        // covers the hover states too, so they don't turn a selected row light
        ...(isSelected && {
          [`&, &:focus-within, ${hoverStates}`]: selectedSx,
        }),
        '&.sortable-chosen': { boxShadow: 1 },
        '&.sortable-ghost': { opacity: '0.5 !important' },
      }}
    >
      <CustomIconButton
        className="objectDragHandle handle"
        data-testid={`drawingToolForm-objectDragHandle-${featureIndex}`}
        disableTouchRipple
        disabled={isLoading}
        sx={{ cursor: 'grab' }}
        tabIndex={-1}
        tooltipTitle={t('drawing-tool-form-object-drag')}
      >
        <DragHandle />
      </CustomIconButton>
      <CustomIconButton
        data-testid={`drawingToolForm-objectVisibility-${featureIndex}`}
        disabled={isLoading}
        onClick={(): void => onToggleVisibility(featureId)}
        tooltipTitle={
          isHidden
            ? t('drawing-tool-form-object-show')
            : t('drawing-tool-form-object-hide')
        }
      >
        {isHidden ? <VisibilityOff /> : <Visibility />}
      </CustomIconButton>
      <Box
        onClick={(): void => nameInputRef.current?.focus()}
        sx={{ flex: 1, minWidth: 0, paddingX: 0.5 }}
      >
        <ReactHookFormTextField
          disabled={isLoading}
          inputSlotProps={{ disableUnderline: true }}
          isReadOnly={false}
          name={`features.${index}.objectName`}
          onBlur={onBlurName}
          onFocus={(): void => onSelect(featureId)}
          onKeyDown={(event): void => {
            event.stopPropagation();
            if (event.key === 'Escape') {
              onDeselect(featureId);
            }
            if (event.key === 'Enter' || event.key === 'Escape') {
              (event.target as HTMLInputElement).blur();
            }
          }}
          rules={{
            required: t('drawing-tool-form-object-name-required'),
            maxLength: {
              value: MAX_OBJECT_NAME_LENGTH,
              message: t('drawing-tool-form-object-name-max-length', {
                maxLength: MAX_OBJECT_NAME_LENGTH,
              }),
            },
            validate: {
              unique: (value, formValues): boolean | string =>
                !isDuplicateObjectName(
                  value as string,
                  (
                    (formValues.features ?? []) as DrawingToolObjectFormValue[]
                  ).map((formObject) => formObject?.objectName ?? ''),
                  index,
                ) || t('drawing-tool-form-object-name-unique'),
            },
          }}
          size="small"
          slotProps={{
            htmlInput: {
              ref: nameInputRef,
              'aria-label': t('drawing-tool-form-object-name'),
            },
          }}
          sx={objectNameSx(isHidden)}
          variant="standard"
        />
      </Box>
      <ToggleMenu
        menuItems={[
          {
            text: t('drawing-tool-form-object-delete'),
            icon: <Delete />,
            action: (): void => onDelete(featureId),
          },
        ]}
        menuPosition="bottom"
        // the menu backdrop covers the page, so the row misses the mouse leaving
        onClose={(): void => onHover(undefined)}
        renderButton={({ onClick, isOpen }): React.ReactNode => (
          <CustomIconButton
            aria-expanded={isOpen}
            aria-haspopup="menu"
            className="objectOptions"
            data-testid={`drawingToolForm-objectOptions-${featureIndex}`}
            disabled={isLoading}
            onClick={onClick}
            tooltipTitle={t('drawing-tool-form-object-options')}
          >
            <Options />
          </CustomIconButton>
        )}
      />
      <ReactHookFormHiddenInput name={`features.${index}.featureId`} />
    </Box>
  );
};

// ReactSortable needs a ref-forwarding tag to attach to
const ObjectList = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>((props, ref) => {
  const { t } = useWarningsTranslation();
  return (
    <Box
      aria-label={t('drawing-tool-form-objects')}
      component="ul"
      ref={ref}
      sx={{ margin: 0, padding: 0, paddingBottom: 1, listStyle: 'none' }}
      {...props}
    />
  );
});

interface DrawingToolFormObjectsProps {
  drawingObjects: DrawingObject[];
  fields: FieldArrayWithId<DrawingToolFormValues, 'features', 'id'>[];
  isLoading?: boolean;
  isNewDrawing: boolean;
  lastUpdatedTime?: string;
  onBlurName: (event: React.FocusEvent<HTMLInputElement>) => void;
  selectedFeatureIds: string[];
  objectActions: DrawingObjectActions;
}

export const DrawingToolFormObjects: React.FC<DrawingToolFormObjectsProps> = ({
  drawingObjects,
  fields,
  isLoading,
  isNewDrawing,
  lastUpdatedTime,
  selectedFeatureIds,
  onBlurName,
  objectActions,
}) => {
  const { t } = useWarningsTranslation();
  const [isExpanded, setIsExpanded] = React.useState(true);
  const toggleExpanded = (): void => setIsExpanded((expanded) => !expanded);
  const hasObjects = fields.length > 0;
  const drawingObjectsById = React.useMemo(
    () => new Map(drawingObjects.map((object) => [object.featureId, object])),
    [drawingObjects],
  );
  const rows = fields.flatMap(({ featureId }, index) => {
    const drawingObject = drawingObjectsById.get(featureId);
    return drawingObject ? [{ drawingObject, index }] : [];
  });
  const featureIds = rows.map(({ drawingObject }) => drawingObject.featureId);
  const caption = lastUpdatedTime
    ? getUtcDateLabel(lastUpdatedTime)
    : isNewDrawing && t('drawing-tool-form-unsaved');

  return (
    <Box
      sx={{
        backgroundColor: 'geowebColors.cards.cardContainer',
        border: 1,
        borderColor: 'geowebColors.cards.cardContainerBorder',
      }}
    >
      {/* Hand-rolled instead of CustomAccordion: an AccordionSummary is a button, so
          the inline drawing name input would nest in it and toggle it, and its
          elevation styling doesn't match this card design. */}
      <Box
        onClick={hasObjects ? toggleExpanded : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 44,
          borderLeft: 4,
          borderLeftColor: 'geowebColors.buttons.primary.default.fill',
          paddingLeft: 1,
          paddingRight: 0.5,
          cursor: hasObjects ? 'pointer' : 'default',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ReactHookFormTextField
            disabled={isLoading}
            inputSlotProps={{ disableUnderline: true }}
            isReadOnly={false}
            name="objectName"
            onBlur={onBlurName}
            onClick={(event): void => event.stopPropagation()}
            onKeyDown={(event): void => event.stopPropagation()}
            rules={{ required: true }}
            size="small"
            slotProps={{
              htmlInput: {
                'aria-label': t('drawing-tool-form-drawing-name'),
                placeholder: t('drawing-tool-form-drawing-name'),
              },
            }}
            sx={{
              '.MuiInputBase-input': {
                ...inlineInputSx,
                '&:hover': {
                  backgroundColor: 'geowebColors.cards.cardContainerMouseOver',
                },
                '&:focus': {
                  outline: '2px solid',
                  outlineColor: 'geowebColors.buttons.primary.default.fill',
                },
              },
            }}
            variant="standard"
          />
          <ReactHookFormHiddenInput name="id" />
          {caption ? (
            <Typography
              sx={{ display: 'block', paddingX: 0.5, opacity: 0.67 }}
              variant="caption"
            >
              {caption}
            </Typography>
          ) : null}
        </Box>
        {hasObjects ? (
          <CustomIconButton
            aria-expanded={isExpanded}
            data-testid="drawingToolForm-objectsToggle"
            onClick={(event): void => {
              // the header would toggle it back
              event.stopPropagation();
              toggleExpanded();
            }}
            tooltipTitle={t('drawing-tool-form-objects-toggle')}
          >
            {isExpanded ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        ) : null}
      </Box>
      {hasObjects && isExpanded ? (
        <ReactSortable
          animation={150}
          direction="vertical"
          disabled={isLoading}
          forceFallback={false}
          handle=".handle"
          list={featureIds.map((id) => ({ id }))}
          // native drag and drop can skip the dragged row's mouseleave
          onEnd={(): void => objectActions.onHover(undefined)}
          setList={(newList): void => {
            const newFeatureIds = newList.map(({ id }) => `${id}`);
            if (!isEqual(newFeatureIds, featureIds)) {
              objectActions.onReorder(newFeatureIds);
            }
          }}
          tag={ObjectList}
        >
          {rows.map(({ drawingObject, index }) => (
            <ObjectRow
              drawingObject={drawingObject}
              index={index}
              isLoading={isLoading}
              isSelected={selectedFeatureIds.includes(drawingObject.featureId)}
              key={fields[index].id}
              objectActions={objectActions}
            />
          ))}
        </ReactSortable>
      ) : null}
    </Box>
  );
};
